import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AssistPanel } from "./components/AssistPanel";
import { CodeEditor } from "./components/CodeEditor";
import { MarkdownPane } from "./components/MarkdownPane";
import { ResizeHandle } from "./components/ResizeHandle";
import { Sidebar } from "./components/Sidebar";
import { TerminalPane } from "./components/TerminalPane";
import { resolveSession, sessionApi, type Session, type SessionApi } from "./lib/api";
import { explain, isRefusal, refusalTitle } from "./lib/refusal";
import { clamp, loadLayout, saveLayout, type Layout } from "./lib/layout";
import { watchClipboard } from "./lib/paste";
import { useLsp } from "./lib/useLsp";

function isMarkdown(path: string): boolean {
  return /\.(md|markdown)$/i.test(path);
}

interface OpenFile {
  path: string;
  saved: string;
  draft: string;
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [bootError, setBootError] = useState<unknown>(null);
  const [files, setFiles] = useState<OpenFile[]>([]);
  const [activePath, setActivePath] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [terminalUp, setTerminalUp] = useState(false);
  const [treeToken, setTreeToken] = useState(0);
  const [reveal, setReveal] = useState<number | null>(null);
  // Which .md files the reader has switched to source. Preview is the default,
  // because instructions are for reading, but editing must stay possible.
  const [asSource, setAsSource] = useState<Set<string>>(() => new Set());
  const [layout, setLayout] = useState<Layout>(() => loadLayout());

  const [noSession, setNoSession] = useState(false);

  useEffect(() => {
    resolveSession()
      .then((found) => (found ? setSession(found) : setNoSession(true)))
      .catch(setBootError);
  }, []);

  // Open what the project nominates, once, so the candidate arrives at the
  // instructions rather than at an empty pane.
  const openedEntrypoints = useRef(false);

  const api: SessionApi | null = useMemo(
    () => (session ? sessionApi(session.id) : null),
    [session],
  );

  const lsp = useLsp(api ? api.lspUrl() : null);

  const active = files.find((f) => f.path === activePath) ?? null;
  const dirtyCount = files.filter((f) => f.draft !== f.saved).length;

  const resize = useCallback((pane: keyof Layout, delta: number) => {
    setLayout((prev) => {
      const next = { ...prev, [pane]: clamp(pane, prev[pane] + delta, prev) };
      saveLayout(next);
      return next;
    });
  }, []);

  const open = useCallback(
    async (path: string, line?: number) => {
      if (!api) return;
      setActivePath(path);
      setReveal(line ?? null);
      if (files.some((f) => f.path === path)) return;
      try {
        const content = await api.read(path);
        setFiles((prev) => [...prev, { path, saved: content, draft: content }]);
        lsp.didOpen(path, content);
      } catch (err) {
        setError(explain(err));
        setActivePath(null);
      }
    },
    [api, files, lsp],
  );

  function edit(path: string, draft: string) {
    setFiles((prev) => prev.map((f) => (f.path === path ? { ...f, draft } : f)));
    lsp.didChange(path, draft);
  }

  const save = useCallback(async () => {
    if (!api) return;
    const file = files.find((f) => f.path === activePath);
    if (!file || file.draft === file.saved) return;
    try {
      await api.write(file.path, file.draft);
      setFiles((prev) =>
        prev.map((f) => (f.path === file.path ? { ...f, saved: f.draft } : f)),
      );
      setTreeToken((n) => n + 1); // a save may have created the file
      setError(null);
    } catch (err) {
      setError(explain(err));
    }
  }, [api, activePath, files]);

  // Monaco's addCommand runs once, at mount, and would capture the `save` from
  // that first render -- when no file was open, so it saved nothing, forever.
  // The ref keeps the bound command pointing at the current closure.
  const saveRef = useRef(save);
  useEffect(() => {
    saveRef.current = save;
  }, [save]);
  const requestSave = useCallback(() => void saveRef.current(), []);

  // Also catch the shortcut when focus is in the terminal or the file tree, where
  // Monaco never sees the keystroke and the browser would offer to save the page.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        requestSave();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [requestSave]);

  useEffect(() => {
    if (!api || !session || openedEntrypoints.current) return;
    const wanted = session.entrypoint_open ?? [];
    if (!wanted.length) return;
    openedEntrypoints.current = true;
    (async () => {
      for (const path of wanted) {
        try {
          const content = await api.read(path);
          setFiles((prev) =>
            prev.some((f) => f.path === path)
              ? prev
              : [...prev, { path, saved: content, draft: content }],
          );
          lsp.didOpen(path, content);
        } catch {
          // A manifest naming a file that does not exist should not blank the
          // editor; the rest still opens.
        }
      }
      setActivePath(wanted[0]);
    })();
  }, [api, session, lsp]);

  // Work arriving from outside the editor is the one thing no server can see.
  const activePathRef = useRef<string | null>(null);
  useEffect(() => {
    activePathRef.current = activePath;
  }, [activePath]);
  useEffect(() => {
    if (!api) return;
    return watchClipboard(api, () => activePathRef.current);
  }, [api]);

  // A window that shrank below the stored sizes would leave panes unreachable.
  useEffect(() => {
    function onWindowResize() {
      setLayout((prev) => ({
        sidebar: clamp("sidebar", prev.sidebar, prev),
        assist: clamp("assist", prev.assist, prev),
        terminal: clamp("terminal", prev.terminal, prev),
      }));
    }
    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, []);

  const submitted = session?.state === "submitted" || session?.state === "expired";
  const paused = session?.paused_at != null && !submitted;

  async function resume() {
    if (!api) return;
    await api.resume();
    setSession(await api.session());
  }

  function toggleSource(path: string, source: boolean) {
    setAsSource((prev) => {
      const next = new Set(prev);
      if (source) next.add(path);
      else next.delete(path);
      return next;
    });
  }

  /** Re-read a file the assistant changed, so the editor is not showing a ghost. */
  const reloadFromDisk = useCallback(
    async (path: string) => {
      if (!api) return;
      try {
        const content = await api.read(path);
        setFiles((prev) =>
          prev.map((f) => (f.path === path ? { ...f, saved: content, draft: content } : f)),
        );
        lsp.didChange(path, content);
      } catch (err) {
        setError(explain(err));
      }
    },
    [api, lsp],
  );

  function close(path: string) {
    lsp.didClose(path);
    setFiles((prev) => prev.filter((f) => f.path !== path));
    setActivePath((current) => {
      if (current !== path) return current;
      const remaining = files.filter((f) => f.path !== path);
      return remaining.length ? remaining[remaining.length - 1].path : null;
    });
  }

  if (bootError) {
    // Two different failures, and only one of them is about the network. A
    // candidate told "could not reach the control plane" about an expired link
    // goes and checks their wifi, which is not the thing that is wrong.
    if (!isRefusal(bootError)) {
      return <div className="empty">could not reach the control plane: {explain(bootError)}</div>;
    }
    return (
      <div className="empty">
        <div style={{ textAlign: "center", lineHeight: 1.6 }}>
          <div>{refusalTitle(bootError)}</div>
          <div style={{ color: "var(--fg-dim)", fontSize: 12, marginTop: 8 }}>
            Ask your interviewer for a new link. Your work up to now is saved.
          </div>
        </div>
      </div>
    );
  }
  if (noSession) {
    return (
      <div className="empty">
        <div style={{ textAlign: "center", lineHeight: 1.6 }}>
          <div>This link does not name a session.</div>
          <div style={{ color: "var(--fg-dim)", fontSize: 12, marginTop: 8 }}>
            Sessions are created in the{" "}
            <a href="/admin" style={{ color: "#7fd1f5" }}>
              admin page
            </a>
            , which issues the candidate a link.
          </div>
        </div>
      </div>
    );
  }
  if (!session || !api) {
    return <div className="empty">opening the session…</div>;
  }

  return (
    <div
      className="shell"
      style={{
        // minmax(0, ...) rather than a bare 1fr: a grid item's automatic minimum
        // is its min-content width, and Monaco and xterm both have a large one.
        // With a bare 1fr the centre refuses to shrink, so growing a side pane
        // overflows the grid sideways instead of moving anything -- the pane's
        // width changes (its text rewraps) while it appears locked in place.
        gridTemplateColumns: `${layout.sidebar}px 5px minmax(0, 1fr) 5px ${layout.assist}px`,
        gridTemplateRows: `minmax(0, 1fr) 5px ${layout.terminal}px 22px`,
      }}
    >
      {paused && (
        <div className="paused-scrim" role="alertdialog" aria-label="Session paused">
          <div>
            <h2>Paused</h2>
            {/* The workspace is covered, not merely disabled: a pause you can
                read the problem through is thinking time, and it is free. */}
            <p>
              Your clock is stopped. The editor, the terminal and the assistant are
              frozen until you come back.
            </p>
            <button className="hand-in" onClick={resume}>
              Resume
            </button>
          </div>
        </div>
      )}

      <Sidebar
        api={api}
        session={session}
        onSession={setSession}
        unsaved={dirtyCount}
        activePath={activePath}
        refreshToken={treeToken}
        onOpen={open}
        onError={setError}
      />
      <div className="split split-left">
        <ResizeHandle axis="x" label="Resize explorer" onResize={(d) => resize("sidebar", d)} />
      </div>

      <main className="editor-area">
        {submitted && (
          <div className="submitted-banner">
            This session has been submitted. Your work is saved and the recording is
            closed — editing and the terminal are no longer available.
          </div>
        )}

        <div className="tabs" role="tablist">
          {files.map((file) => (
            <button
              key={file.path}
              role="tab"
              className="tab"
              aria-selected={file.path === activePath}
              onClick={() => setActivePath(file.path)}
              onAuxClick={(event) => event.button === 1 && close(file.path)}
            >
              <span>{file.path.split("/").pop()}</span>
              <span
                className="dirty"
                onClick={(event) => {
                  event.stopPropagation();
                  close(file.path);
                }}
              >
                {file.draft === file.saved ? "×" : "●"}
              </span>
            </button>
          ))}
        </div>

        {error && <div className="error">{error}</div>}

        {active && isMarkdown(active.path) && (
          <div className="view-toggle">
            <button
              className="chip"
              aria-pressed={!asSource.has(active.path)}
              onClick={() => toggleSource(active.path, false)}
            >
              Preview
            </button>
            <button
              className="chip"
              aria-pressed={asSource.has(active.path)}
              onClick={() => toggleSource(active.path, true)}
            >
              Source
            </button>
          </div>
        )}

        {active ? (
          isMarkdown(active.path) && !asSource.has(active.path) ? (
            <MarkdownPane source={active.draft} />
          ) : (
            <CodeEditor
              path={active.path}
              value={active.draft}
              revealLine={reveal}
              onChange={(draft) => edit(active.path, draft)}
              onSave={requestSave}
            />
          )
        ) : (
          <div className="empty">Open a file from the explorer</div>
        )}
      </main>

      <div className="split split-horizontal">
        <ResizeHandle
          axis="y"
          invert
          label="Resize terminal"
          onResize={(d) => resize("terminal", d)}
        />
      </div>

      <TerminalPane api={api} onStatus={setTerminalUp} />

      <div className="split split-right">
        <ResizeHandle
          axis="x"
          invert
          label="Resize AI panel"
          onResize={(d) => resize("assist", d)}
        />
      </div>

      <AssistPanel
        api={api}
        activePath={active?.path ?? null}
        activeContents={active?.draft ?? null}
        onApplied={() => {
          // The assistant wrote to disk; the editor's copy is now stale.
          setTreeToken((n) => n + 1);
          if (active) void reloadFromDisk(active.path);
        }}
      />

      <footer className="status">
        <span>{terminalUp ? "terminal connected" : "terminal offline"}</span>
        <span>{dirtyCount > 0 ? `${dirtyCount} unsaved` : "all saved"}</span>
        <span>recording · {session.id.slice(0, 8)}</span>
        <span>{lsp.up ? "pyright ready" : "pyright offline"}</span>
        <span className="spacer" />
        <span>{active?.path ?? "no file open"}</span>
      </footer>
    </div>
  );
}
